import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';
import { addMessageToCurrentActivity } from '../../redux/classroomSlice'; // We'll reuse classroom activity chat
import { normalizeStudentExamPayload, toLegacyQuestionShape } from './models/examQuestionModel';

const loadPrompt = async (fileName) => {
  try {
    const response = await fetch(`/prompts/${fileName}`);
    if (!response.ok) {
      throw new Error(`Failed to load prompt file: ${fileName}`);
    }
    return await response.text();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('❌ Error loading prompt file:', error);
    return null;
  }
};

export const ExaminerService = {
  async generateStudentExam({ lessonTitle, courseDetails = null, questionCount = 5 }) {
    if (!API_BASE_URL) {
      throw new Error('Backend URL is not configured.');
    }

    const topic =
      String(lessonTitle || '')
        .trim()
        .replace(/\s+/g, ' ') ||
      String(courseDetails?.unitTitle || '').trim() ||
      String(courseDetails?.title || '').trim() ||
      'Untitled Lesson';

    const sessionId = `sess_${Date.now()}`;
    const endpoints = ['/generate-exam', '/exam/generate', '/mostlearned/generate-exam'];
    const normalizedCourseTitle = String(courseDetails?.title || '').trim();
    const normalizedUnitId = String(courseDetails?.unitId || '').trim();
    const normalizedUnitTitle = String(courseDetails?.unitTitle || '').trim();
    const normalizedUnitObjective = String(
      courseDetails?.unitObjective || courseDetails?.objective || ''
    )
      .trim()
      .replace(/\s+/g, ' ');
    const payload = {
      sessionId,
      topic,
      questionCount: Math.max(1, Number(questionCount) || 5),
      courseId: courseDetails?.id || null,
      courseTitle: normalizedCourseTitle || null,
      unitId: normalizedUnitId || null,
      unitTitle: normalizedUnitTitle || null,
      unitObjective: normalizedUnitObjective || null,
    };

    const attemptErrors = [];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errPayload = await response.json().catch(() => null);
          attemptErrors.push(
            errPayload?.error ||
              errPayload?.code ||
              `Exam service request failed at ${endpoint}: ${response.status} ${response.statusText}`
          );
          continue;
        }

        const data = await response.json();
        const normalized = normalizeStudentExamPayload(data);
        if (normalized.questions.length > 0) return normalized;
        attemptErrors.push(`Exam service returned no questions at ${endpoint}.`);
      } catch (_error) {
        attemptErrors.push(`Could not reach exam service at ${endpoint}.`);
      }
    }

    throw new Error(
      attemptErrors.length > 0 ? attemptErrors.join(' ') : 'Exam service is currently unavailable.'
    );
  },

  async generateContentForActivity(activity) {
    try {
      const lessonTitle = activity.title || activity.lessonTitle || activity.id;
      const courseDetails = activity.courseDetails || null;
      const questionCount = activity.questionCount || 5;
      const examPayload = await this.generateStudentExam({
        lessonTitle,
        courseDetails,
        questionCount,
      });
      const questions = examPayload.questions.map(toLegacyQuestionShape);
      return Array.isArray(questions) ? questions : [];
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('❌ generateContentForActivity (Examiner) failed:', err);
      return [];
    }
  },

  async processStudentQuestion(
    question,
    currentQuestion,
    chatHistory,
    studentProfile,
    examState = {},
    dispatch
  ) {
    const examContext = {
      isExamActive: examState?.isExamActive ?? false,
      currentQuestionIndex: examState?.currentQuestionIndex ?? 0,
      totalQuestions: examState?.questions?.length ?? 0,
      timeRemaining: examState?.timeRemaining ?? 0,
      score: examState?.score ?? 0,
    };

    const questionPhase = !currentQuestion
      ? 'pre_exam'
      : examState?.submitted
        ? 'post_exam'
        : examState.isExamActive
          ? 'during_exam'
          : 'reviewing';

    try {
      const prompt = await loadPrompt('examinerChatPrompt.txt');
      if (!prompt) return null;

      const payload = {
        agentId: 'examiner',
        persona: studentProfile?.persona || null,
        model: 'deepseek-chat',
        messages: [
          { role: 'system', content: prompt },
          {
            role: 'user',
            content: JSON.stringify({
              question,
              currentQuestion,
              chatHistory,
              studentProfile,
              examContext,
              questionPhase,
            }),
          },
        ],
        temperature: 0.7,
      };

      const response = await fetch(`${API_BASE_URL}/examiner-chat`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      const studentMessage = {
        sender: 'student',
        text: question,
        timestamp: new Date().toLocaleTimeString(),
      };

      const examinerResponse = {
        sender: 'Examiner',
        text: data.response,
        timestamp: new Date().toLocaleTimeString(),
      };

      dispatch(addMessageToCurrentActivity(studentMessage));
      dispatch(addMessageToCurrentActivity(examinerResponse));

      return examinerResponse;
    } catch (error) {
      return null;
    }
  },

  async generateExamQuestions(lessonTitle, studentProfile, courseDetails, questionCount = 5) {
    try {
      const normalized = await this.generateStudentExam({
        lessonTitle,
        courseDetails,
        questionCount,
      });
      return normalized.questions.map(toLegacyQuestionShape);
    } catch (error) {
      return null;
    }
  },

  async submitStudentSession({ sessionId, examId, userId, answers }) {
    const payload = { sessionId, answers };
    if (examId) payload.examId = examId;
    if (userId) payload.userId = userId;
    const endpoints = ['/submit-session', '/exam/submit-session', '/mostlearned/submit-session'];
    const attemptErrors = [];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errPayload = await response.json().catch(() => null);
          attemptErrors.push(
            errPayload?.error ||
              `Exam submission failed at ${endpoint}: ${response.status} ${response.statusText}`
          );
          continue;
        }

        return response.json();
      } catch (_error) {
        attemptErrors.push(`Could not reach exam submission service at ${endpoint}.`);
      }
    }

    throw new Error(
      attemptErrors.length > 0
        ? attemptErrors.join(' ')
        : 'Exam submission service is currently unavailable.'
    );
  },

  async submitExam(studentAnswers, examId) {
    const prompt = await loadPrompt('examinerGradingPrompt.txt');
    if (!prompt) return null;

    const payload = {
      agentId: 'examiner',
      persona: studentAnswers?.persona || null,
      model: 'deepseek-chat',
      messages: [
        { role: 'system', content: prompt },
        {
          role: 'user',
          content: JSON.stringify({
            examId,
            studentAnswers,
          }),
        },
      ],
      temperature: 0.3,
    };

    try {
      const response = await fetch(`${API_BASE_URL}submit-exam`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('❌ Error submitting exam:', error);
      return null;
    }
  },

  async getExamDetails(examId) {
    try {
      const response = await fetch(`${API_BASE_URL}exam-details/${examId}`, {
        method: 'GET',
        headers: DEFAULT_HEADERS,
      });

      const data = await response.json();
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('❌ Error fetching exam details:', error);
      return null;
    }
  },
};
